import { atom } from "jotai";

import { env } from "../env";

const TIMEOUT_MS = 5_000; // 5 seconds
const REFRESH_INTERVAL_MS = 30_000; // every 30 seconds

const checkInternetConnection = async (signal?: AbortSignal): Promise<boolean> => {
	// Merge TanStack Query's signal with our own 5s timeout
	const controller = new AbortController();

	const onAbort = () => controller.abort();

	if (signal) {
		if (signal.aborted) {
			controller.abort();
		} else {
			signal.addEventListener("abort", onAbort);
		}
	}

	const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
	try {
		const headers: HeadersInit = {};
		if (env.userAgent) {
			headers["X-Moment-User-Agent"] = env.userAgent;
		}

		const response = await fetch(env.connectionCheckUrl, {
			method: "GET",
			signal: controller.signal,
			headers,
		});
		return response.ok;
	} catch (err) {
		console.error("Error checking internet connection", err);
		return false;
	} finally {
		clearTimeout(timeoutId);
		if (signal) {
			signal.removeEventListener("abort", onAbort);
		}
	}
};

//
// Start optimistic (assume connected) until first check completes.
//
// IMPORTANT: This *MUST* be synchronous, for several reasons.
//
// We *DO NOT* want to wait a second or two on startup to check for the Internet connection before
// running things like docs list.
//
export const $internet = atom(true);
$internet.debugLabel = "internet";

/**
 * Dev toggle: when true, `$workflowSchedule` treats the app as offline
 * regardless of the real connection state. Useful for testing pause UI.
 */
export const $simulateOffline = atom(false);
$simulateOffline.debugLabel = "simulateOffline";

$internet.onMount = (set) => {
	let lastCheckTime = 0;
	// Monotonically increasing version; stale async results are discarded.
	let checkVersion = 0;

	const runCheck = () => {
		const now = Date.now();
		const timeSinceLastCheck = now - lastCheckTime;
		// Only run if enough time has passed since last check
		if (timeSinceLastCheck >= REFRESH_INTERVAL_MS) {
			lastCheckTime = now;
			const version = ++checkVersion;
			void checkInternetConnection().then((result) => {
				if (version === checkVersion) {
					set(result);
				}
			});
		}
	};

	// Run check immediately on mount
	runCheck();

	// Then check every 30 seconds
	// Note: setInterval may fire rapidly when tab becomes active after being backgrounded
	// The timestamp check in runCheck prevents this from causing rapid requests
	const intervalId = setInterval(runCheck, REFRESH_INTERVAL_MS);

	// React instantly to browser online/offline events instead of waiting up to 30s
	const onOnline = () => {
		lastCheckTime = 0; // Reset so runCheck fires immediately
		runCheck();
	};
	const onOffline = () => {
		// Bump version to discard any in-flight async check that would overwrite this
		++checkVersion;
		set(false);
	};

	if (typeof window !== "undefined") {
		window.addEventListener("online", onOnline);
		window.addEventListener("offline", onOffline);
	}

	return () => {
		clearInterval(intervalId);
		if (typeof window !== "undefined") {
			window.removeEventListener("online", onOnline);
			window.removeEventListener("offline", onOffline);
		}
	};
};
